// ============================================================================
// SaveHotelModal.jsx — THE ONLY Save-Hotel modal in the app.
//
// ⚠️ DO NOT DUPLICATE — this is the only Save Hotel modal in the app ⚠️
//
// Used by:
//   • Journey page (`SAVE MY HOTEL →` card)
//   • Car / Ride page (hotel card tap + dashed prompt tap)
//   • Anywhere else hotel saving is needed
//
// OFFLINE-FIRST:
//   • The modal opens, runs, and saves with NO internet connection.
//   • GPS uses `navigator.geolocation.getCurrentPosition` — works offline.
//   • The hotel is written to `localStorage["habano.hotel"]` immediately —
//     no API call is required for a successful save.
//   • Hotel pickers read from the offline business cache only.
//   • If the user is signed in AND `navigator.onLine === true`, we
//     fire-and-forget a POST to /me/saved-locations after the local write.
//   • If offline at save time, a pending-sync flag is left so the next
//     online session can flush automatically (see `flushPendingHotelSync`).
//
// Save options (all three persist to the same `habano.hotel` key):
//   1. "Save My Hotel Location" — captures GPS, saves with source="gps"
//   2. Pick from cached hotels — saves with source="picker"
//   3. Type a name — saves with source="manual"
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { Search, X, Hotel as HotelIcon, MapPin, Loader2, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { writeHotel, syncHotelIfOnline, readHotel } from "../lib/hotelStore";
import { readCachedBusinesses } from "../lib/businessCache";

export default function SaveHotelModal({ isOpen, onClose, authed, onSaved }) {
  const { t, lang } = useI18n();
  const [query, setQuery] = useState("");
  const [pickedId, setPickedId] = useState(null);
  const [customName, setCustomName] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyKind, setBusyKind] = useState(null); // 'gps' | 'submit'
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);

  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    setPickedId(null);
    setCustomName("");
    setBusy(false);
    setBusyKind(null);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [isOpen]);

  const hotelOptions = useMemo(() => {
    if (!isOpen) return [];
    const cache = readCachedBusinesses() || [];
    return cache
      .filter((b) => b.category === "hotel" || b.category === "airbnb")
      .filter((b) => {
        const q = query.trim().toLowerCase();
        if (!q) return true;
        return (b.name || "").toLowerCase().includes(q);
      })
      .slice(0, 25);
  }, [isOpen, query]);

  if (!isOpen) return null;

  // --- Save handlers ----------------------------------------------------
  const persist = (hotel) => {
    // Always write locally first — this is the source of truth.
    writeHotel(hotel);
    const saved = readHotel();
    // Background sync to API (only if online + authed). Fire-and-forget.
    if (authed && online) {
      syncHotelIfOnline(true).catch(() => {});
    }
    toast.success(lang === "es" ? "Hotel guardado" : "Hotel saved");
    onSaved?.(saved);
    onClose?.();
  };

  const saveViaGps = () => {
    if (!("geolocation" in navigator)) {
      toast.error(lang === "es" ? "GPS no disponible" : "GPS not available");
      return;
    }
    setBusy(true); setBusyKind("gps");
    // 12s GPS timeout. `enableHighAccuracy` keeps the fix tight but works offline.
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        persist({
          name: customName.trim() || (lang === "es" ? "Mi Hotel" : "My Hotel"),
          address: "",
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          source: "gps",
        });
        setBusy(false); setBusyKind(null);
      },
      (err) => {
        setBusy(false); setBusyKind(null);
        toast.error(
          lang === "es"
            ? `No se pudo obtener tu ubicación: ${err.message}`
            : `Could not get your location: ${err.message}`
        );
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const saveViaPick = (b) => {
    setBusy(true); setBusyKind("submit");
    persist({
      name: b.name,
      address: b.address || b.neighborhood || "",
      lat: b.lat ?? 0,
      lng: b.lng ?? 0,
      source: "picker",
    });
    setBusy(false); setBusyKind(null);
  };

  const saveViaCustom = () => {
    const name = customName.trim();
    if (name.length < 2) return;
    setBusy(true); setBusyKind("submit");
    persist({
      name,
      address: "",
      lat: 0,
      lng: 0,
      source: "manual",
    });
    setBusy(false); setBusyKind(null);
  };

  const picked = hotelOptions.find((b) => b.id === pickedId) || null;
  const canSubmit = !busy && (picked || customName.trim().length >= 2);
  const submit = () => {
    if (picked) saveViaPick(picked);
    else if (customName.trim().length >= 2) saveViaCustom();
  };

  return (
    <>
      <button
        type="button"
        aria-label="Dismiss"
        data-testid="save-hotel-modal-scrim"
        onClick={onClose}
        className="fixed inset-0 z-[80]"
        style={{ background: "rgba(0,0,0,0.72)" }}
      />
      <div
        data-testid="save-hotel-modal"
        className="fixed left-0 right-0 bottom-0 z-[81]"
        style={{
          background: "#171717",
          borderTop: "1px solid rgba(232,184,75,0.3)",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Fixed header */}
        <div style={{ position: "relative", flex: "0 0 auto" }}>
          <div className="grid place-items-center pt-3 pb-2">
            <span style={{ width: 44, height: 4, borderRadius: 9999, background: "rgba(232,184,75,0.45)" }} />
          </div>
          <div className="px-6 pt-2 pb-3">
            <h2 className="pr-12" data-testid="save-hotel-title"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: "#F5F1E8", lineHeight: 1.25 }}>
              {lang === "es" ? "Guardar Mi Hotel" : "Save My Hotel"}
            </h2>
            <p className="mt-1 flex items-center gap-2"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: online ? "#B0AAA3" : "#F5A623" }}>
              {!online && <WifiOff className="w-3.5 h-3.5" strokeWidth={1.5} />}
              <span>
                {online
                  ? (lang === "es"
                    ? "Usa GPS, busca, o escribe el nombre."
                    : "Use GPS, search, or type the name.")
                  : (lang === "es"
                    ? "Sin conexión — se guardará localmente."
                    : "Offline — will be saved locally.")}
              </span>
            </p>
          </div>
          <button
            type="button"
            data-testid="save-hotel-close"
            onClick={onClose}
            aria-label="Close"
            className="grid place-items-center"
            style={{
              position: "absolute", top: 12, right: 14,
              width: 28, height: 28, borderRadius: "50%",
              background: "#222", color: "#fff", border: "none", zIndex: 90,
              boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
            }}
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: "1 1 auto", overflowY: "auto", minHeight: 0 }} className="px-6 pb-3">
          {/* GPS button — primary CTA */}
          <button
            type="button"
            data-testid="save-hotel-gps"
            onClick={saveViaGps}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 transition-opacity"
            style={{
              height: 52,
              background: "linear-gradient(180deg, #F5CC55, #E8B84B)",
              color: "#0B0B0B",
              border: "none",
              borderRadius: 12,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              fontWeight: 700,
              opacity: busy ? 0.55 : 1,
            }}
          >
            {busyKind === "gps"
              ? <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />{lang === "es" ? "Obteniendo ubicación…" : "Getting location…"}</>
              : <><MapPin className="w-4 h-4" strokeWidth={2} />{lang === "es" ? "Guardar Ubicación de Mi Hotel" : "Save My Hotel Location"}</>
            }
          </button>

          {/* Divider */}
          <div className="my-5 flex items-center gap-3" aria-hidden="true">
            <span style={{ flex: 1, height: 1, background: "rgba(232,184,75,0.15)" }} />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3", letterSpacing: "0.06em", textTransform: "uppercase" }}>
              {lang === "es" ? "O" : "Or"}
            </span>
            <span style={{ flex: 1, height: 1, background: "rgba(232,184,75,0.15)" }} />
          </div>

          {/* Search field */}
          <div className="flex items-center gap-3"
            style={{
              background: "#0B0B0B",
              border: "1px solid rgba(232,184,75,0.3)",
              borderRadius: 12,
              padding: "0 14px",
              height: 48,
            }}
          >
            <Search className="w-4 h-4 shrink-0" strokeWidth={1.5} style={{ color: "rgba(245,241,232,0.55)" }} />
            <input
              data-testid="save-hotel-search"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPickedId(null); }}
              placeholder={lang === "es" ? "Buscar hotel..." : "Search hotel..."}
              className="flex-1 bg-transparent outline-none"
              style={{ color: "#F5F1E8", fontFamily: "'DM Sans', sans-serif", fontSize: 16 }}
            />
          </div>

          {hotelOptions.length > 0 && (
            <ul className="mt-3 space-y-1.5" data-testid="save-hotel-results">
              {hotelOptions.map((b) => {
                const active = pickedId === b.id;
                return (
                  <li key={b.id}>
                    <button
                      type="button"
                      data-testid={`save-hotel-option-${b.id}`}
                      onClick={() => { setPickedId(b.id); setCustomName(""); }}
                      className="w-full text-left flex items-center gap-3 transition-colors"
                      style={{
                        background: active ? "rgba(232,184,75,0.12)" : "#141414",
                        border: `1px solid ${active ? "#E8B84B" : "rgba(245,241,232,0.08)"}`,
                        borderRadius: 12,
                        padding: "12px 14px",
                        minHeight: 56,
                      }}
                    >
                      <span className="grid place-items-center shrink-0 rounded-full"
                        style={{ width: 36, height: 36, background: "rgba(232,184,75,0.12)" }}>
                        <HotelIcon className="w-4 h-4" style={{ color: "#E8B84B" }} strokeWidth={1.5} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#F5F1E8", fontWeight: 500 }}>
                          {b.name}
                        </div>
                        {b.neighborhood && (
                          <div className="mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3" }}>
                            {b.neighborhood}
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Manual entry */}
          <div className="mt-5">
            <label
              htmlFor="save-hotel-custom"
              className="block mb-2"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#E8B84B", letterSpacing: "0.04em" }}
            >
              {lang === "es" ? "O escribe el nombre" : "Or type the name"}
            </label>
            <input
              id="save-hotel-custom"
              data-testid="save-hotel-custom"
              value={customName}
              onChange={(e) => { setCustomName(e.target.value); setPickedId(null); }}
              placeholder={lang === "es" ? "Nombre del hotel" : "Hotel name"}
              className="w-full bg-transparent outline-none"
              style={{
                background: "#0B0B0B",
                border: "1px solid rgba(232,184,75,0.3)",
                borderRadius: 12,
                padding: "0 14px",
                height: 48,
                color: "#F5F1E8",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16,
              }}
            />
          </div>
        </div>

        {/* Footer submit button */}
        <div className="px-6 pt-3" style={{ borderTop: "1px solid rgba(232,184,75,0.15)", flex: "0 0 auto" }}>
          <button
            type="button"
            data-testid="save-hotel-submit"
            onClick={submit}
            disabled={!canSubmit}
            className="w-full inline-flex items-center justify-center gap-2 transition-opacity"
            style={{
              height: 52,
              background: "transparent",
              color: "#E8B84B",
              border: "1px solid rgba(232,184,75,0.55)",
              borderRadius: 12,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              fontWeight: 600,
              opacity: canSubmit ? 1 : 0.45,
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {busyKind === "submit"
              ? <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />{lang === "es" ? "Guardando…" : "Saving…"}</>
              : (lang === "es" ? "Guardar Hotel" : "Save Hotel")}
          </button>
        </div>
      </div>
    </>
  );
}
